import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { authGuard } from '../auth/guards/auth.guard';
import { SessionService } from '../auth/session.service';
import { fcraGuard } from '../../features/tnc/fcra.guard';
import { orgGuard } from '../user-context/org.guard';

/**
 * Gate for the authenticated shell, running the bootstrap guards STRICTLY in
 * order: session → FCRA/T&C → organization.
 *
 * Angular executes the guards in a `canActivate: [...]` array concurrently — the
 * array order only decides whose return value wins the navigation, NOT the order
 * of their side effects. Listing `[authGuard, fcraGuard, orgGuard]` therefore let
 * orgGuard's profile fetch + org-selection modal race the FCRA modal, so on a
 * refresh the org popup could appear before (or instead of) the T&C. Composing
 * them here forces true sequencing: each guard fully resolves — its modal shown
 * and dismissed — before the next begins.
 *
 * inject() only works during a guard's synchronous run (the context is lost after
 * the first await), so the injector is captured up front and each guard is invoked
 * through runInInjectionContext. Every guard already fails closed (redirects to
 * /signin and returns false) on its own, so a false short-circuits the chain.
 *
 * Once all three pass, the login's audit session is started (POST /sessions/start,
 * after org selection so it carries X-Organization-Id). It's fire-and-forget and a
 * no-op on later loads of the same login, or once switch-org has returned a session
 * id, so it never delays the shell.
 */
export const shellAccessGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean> => {
  const injector = inject(EnvironmentInjector);
  const session = inject(SessionService);
  const run = (guard: CanActivateFn): Promise<boolean> =>
    Promise.resolve(runInInjectionContext(injector, () => guard(route, state)) as boolean);

  if (!(await run(authGuard))) {
    return false;
  }
  if (!(await run(fcraGuard))) {
    return false;
  }
  if (!(await run(orgGuard))) {
    return false;
  }
  session.start();
  return true;
};
